/**
 * Session 12 — Designing your daily rituals
 * Shared by modal, SessionFormResponses, and admin CohortTab.
 */

export const SESSION_NUMBER = 12

export const FORM_TITLE = 'Designing your daily rituals'

export const FORM_SUBTITLE = 'Design your own daily/weekly/monthly wellness rituals'

/** Stored in formData arrays when selected (daily rituals sections) */
export const OPT_OUT_LABEL = 'I do not want this to be part of my daily ritual'

/** Stored in formData for “Personalize Your Wellness Journey” subsections only */
export const OPT_OUT_LABEL_PERSONALIZE_JOURNEY =
  'I do not want this to be part of my personalize journey'

/** Every opt-out string that may appear in saved formData (for mutual-exclusion logic) */
export const SESSION12_ALL_OPT_OUT_LABELS = [OPT_OUT_LABEL, OPT_OUT_LABEL_PERSONALIZE_JOURNEY]

/** Same label as session 4 health form; requires `${sectionId}_othersText` when checked */
export const OTHERS_OPTION = 'Others'

/** Shown once before the personalized journey subsections (modal + response views) */
export const PERSONALIZE_JOURNEY_GROUP_TITLE = 'Personalize Your Wellness Journey'

export const SECTIONS = [
  {
    id: 'morning',
    title: '🌅 Daily Morning Habits (Foundation for the Day)',
    options: [
      'Wake up at a consistent time',
      'Get natural sunlight within 30 minutes of waking',
      'Drink a glass of water',
      'Gentle stretching or joint mobility',
      '5–10 minutes of conscious breathing',
      'Meditate or Pray',
      'Set an intention for the day',
      'Avoid phone/screens for the first 30 minutes',
      'Learning something new for 15-20 mins.',
    ],
  },
  {
    id: 'nutrition',
    title: '🍎 Nutrition & Eating Habits',
    options: [
      'Eat meals at regular times',
      'Prioritise whole, minimally processed foods',
      'Include protein, fibre, and healthy fats daily',
      'Eat slowly and mindfully',
      'Stay hydrated throughout the day',
      'Limit eating while distracted (screens/work)',
      'Stop eating when comfortably full',
    ],
  },
  {
    id: 'movement',
    title: '🚶🏽‍♀️ Movement & Physical Health Habits',
    options: [
      'Walk daily (even if short)',
      'Strength train 2–3 times a week',
      'Tai-chi 2–3 times a week',
      'Yoga 2–3 times a week',
      'Take movement breaks every 60–90 minutes',
      'Pay attention to posture and ergonomics',
      'Balance intense workouts with recovery days',
    ],
  },
  {
    id: 'nervousSystem',
    title: '🌬️ Nervous System & Mental Wellbeing',
    options: [
      'Practice slow breathing when stressed',
      'Notice emotional states without judgment',
      'Journal thoughts or feelings regularly',
      'Spend time in nature weekly',
      'Reduce multitasking',
      'Use the bodyscan technique to notice stress in my body.',
      'Meditate at least 10 mins daily.',
    ],
  },
  {
    id: 'emotionalRelational',
    title: '🤍 Emotional & Relational Health Habits',
    options: [
      'Express emotions instead of suppressing them',
      'Set healthy boundaries with time and energy',
      'Practice deep listening',
      'Stay connected with supportive people',
      'Ask for help when needed',
      'Practice compassion toward yourself',
      'Go for therapy',
      'Get someone to coach me',
    ],
  },
  {
    id: 'eveningSleep',
    title: '🌙 Evening & Sleep Habits',
    options: [
      'Create a consistent wind-down routine',
      'Screen-free evening hour',
      'Yoga Nidra / Meditation / Gentle Stretches',
      'Reduce screen exposure 60–90 minutes before bed',
      'Eat dinner at least 2–3 hours before sleep',
      'Reflect on the day (what worked, what didn’t)',
      'Go to bed and wake up at similar times',
      'Keep the bedroom dark, quiet, and cool',
    ],
  },
  {
    id: 'lifestyle',
    title: '🌱 Lifestyle & Long-Term Wellbeing Habits',
    options: [
      'Take regular digital detox breaks',
      'Spend time on hobbies or creative outlets',
      'Learn something new regularly',
      'Practice gratitude daily',
      'Align daily actions with personal value',
      'Schedule rest without guilt',
    ],
  },
  {
    id: 'selfReflection',
    title: '✨ Self-Reflection Habits',
    options: [
      'Weekly self check-in',
      'Review energy levels (physical, mental, emotional)',
      'Adjust routines based on what feels supportive',
      'Celebrate small wins',
      'Let go of habits that no longer serve you',
      'Recommit gently — without perfection',
    ],
  },
  {
    id: 'journeyMovementBody',
    groupTitle: PERSONALIZE_JOURNEY_GROUP_TITLE,
    title: '🌿 Movement & Body-Based Practices',
    options: [
      'Yoga (Hatha, Vinyasa, Yin, Restorative, Ashtanga)',
      'Somatic movement / Feldenkrais',
      'Tai Chi',
      'Qigong',
      'Pilates',
      'Conscious / Ecstatic dance',
      'Walking meditation',
      'Mobility & joint care rituals',
      'Functional strength training',
      'Animal flow / primal movement',
    ],
  },
  {
    id: 'journeyBreathRegulation',
    title: '🌬️ Breath, Nervous System & Regulation',
    options: [
      'Pranayama',
      'Box breathing / coherent breathing',
      'Conscious breathwork (holotropic, rebirthing)',
      'Vagus nerve toning practices',
      'Cold exposure / ice baths',
      'Heat therapy (sauna, steam)',
      'Contrast therapy (hot–cold)',
      'Yoga Nidra',
    ],
  },
  {
    id: 'journeyMindfulness',
    title: '🧠 Mindfulness, Awareness & Inner Work',
    options: [
      'Mindfulness meditation',
      'Vipassana / insight meditation',
      'Loving-kindness (Metta) practice',
      'Self-inquiry & reflective journaling',
      'Visualization & guided imagery',
      'Gratitude rituals',
      'Body scan meditation',
      'Silent sitting / mini-retreats',
    ],
  },
  {
    id: 'journeySoundEnergy',
    title: '🎵 Sound, Energy & Subtle Practices',
    options: [
      'Sound baths',
      'Mantra chanting',
      'Kirtan',
      'Singing bowls therapy',
      'Gong baths',
      'Reiki / energy healing',
      'Energy cleansing rituals',
      'Chakra meditation',
    ],
  },
  {
    id: 'journeyLifestyleNature',
    title: '🌱 Lifestyle, Nature & Sensory Wellness',
    options: [
      'Forest bathing (Shinrin-yoku)',
      'Sunrise & sunset rituals',
      'Ayurvedic Dinacharya',
      'Herbal teas & tonics',
      'Mindful eating',
      'Digital detox practices',
      'Sleep hygiene rituals',
      'Aromatherapy',
      'Foot soaks & grounding rituals',
    ],
  },
  {
    id: 'journeyEmotionalRelational',
    title: '🤍 Emotional, Relational & Expressive Wellness',
    options: [
      'Therapy / counseling',
      'Inner child healing',
      'Women’s circles',
      'Men’s circles',
      'Group sharing & ritual circles',
      'Art / creative expression therapy',
      'Personal or community ceremonies',
    ],
  },
]

export const SECTION_IDS = SECTIONS.map((s) => s.id)

export function getSectionById(id) {
  return SECTIONS.find((s) => s.id === id)
}

/** Journey subsections use {@link OPT_OUT_LABEL_PERSONALIZE_JOURNEY}; others use {@link OPT_OUT_LABEL}. */
export function getSectionOptOutLabel(section) {
  if (!section) return OPT_OUT_LABEL
  if (section.id?.startsWith('journey')) return OPT_OUT_LABEL_PERSONALIZE_JOURNEY
  return OPT_OUT_LABEL
}

export function stripAllSession12OptOuts(arr) {
  if (!Array.isArray(arr)) return []
  return arr.filter((v) => !SESSION12_ALL_OPT_OUT_LABELS.includes(v))
}

/** Map legacy daily-ritual opt-out on journey sections to the journey-specific wording. */
export function normalizeSession12FormData(data) {
  if (!data || typeof data !== 'object') return {}
  const next = { ...data }
  for (const section of SECTIONS) {
    const key = section.id
    const arr = next[key]
    if (!Array.isArray(arr)) continue
    const journeyOptOut = getSectionOptOutLabel(section)
    if (journeyOptOut === OPT_OUT_LABEL_PERSONALIZE_JOURNEY && arr.includes(OPT_OUT_LABEL)) {
      next[key] = arr.map((v) => (v === OPT_OUT_LABEL ? journeyOptOut : v))
    }
  }
  return next
}

/**
 * Human-readable value for one section’s stored selections.
 * @param {string[]} selected
 * @param {string} othersText
 */
export function formatSession12SectionValue(selected, othersText) {
  if (!selected || selected.length === 0) return '—'
  const parts = selected.map((v) => {
    if (v === OTHERS_OPTION && othersText?.trim()) {
      return `Others (${othersText.trim()})`
    }
    return v
  })
  return parts.join(', ')
}
